<template>
<div class="fehler-korrigieren">
    <ZugStepper :anzahl="uebung.zuege.length" :aktuell="erledigt.length" />
    <AblaufKopf :uebung="uebung" />


    <!-- Fertige Zuege, wie in "Alles sortieren" -->
    <div v-for="z in erledigt" :key="z.nr" class="erledigt" :style="einzug(z)">
        <p class="station nur-vorlesen">{{ z.station.rufname }}</p>
        <p class="spruch">{{ spruchVon(z) }}</p>
    </div>

    <p v-if="uebung.zuege.length > 1" class="station" :style="{ marginLeft: einzug(zug).marginLeft }">{{ zug.station.rufname }}</p>
    <p class="auftrag">Ist dieser Spruch richtig? Korrigiere ihn, wenn nötig, und prüfe ihn.</p>

    <ZugSortieren
        :key="zug.nr"
        :zug="zug"
        :vorgabe="vorgabe"
        :rueckmeldung="rueckmeldung"
        :leer-sperren="leerSperren"
        @pruefen="pruefen"
        @geaendert="rueckmeldung = null">
        <template #rechts><slot name="rechts" /></template>
    </ZugSortieren>

</div>
</template>

<script>
import { ref, computed } from 'vue';
import AblaufKopf from '@/components/AblaufKopf.vue';
import ZugStepper from '@/components/ZugStepper.vue';
import { pruefe, mische } from '@/logic/Uebung';
import { einzugFuer } from '@/logic/Einzug';
import ZugSortieren from '@/components/ZugSortieren.vue';

// Uebungsart "Fehler korrigieren": Zug fuer Zug liegt ein fertiger Spruch in
// der Ablage. In einem oder zwei Zuegen steckt ein Fehler (Ersatz oder
// Tausch), die anderen sind richtig — welche, steht nirgends. Der Lernende
// korrigiert mit denselben Knoepfen wie beim Sortieren und prueft.
//
// Geprueft wird wie beim Sortieren mit pruefe(): die Frage ist dieselbe,
// "ist das der richtige Spruch?".
//
// Im Vorrat liegen der ersetzte richtige Baustein und die Ablenker des Zuges,
// die nicht schon im Spruch stehen. So verraet der Vorrat nicht, ob und wo
// ein Fehler steckt.
//
// Vertrag mit dem Container wie bei jeder Uebungsart (siehe AllesSortieren).
// Die Fehler sind schon gewuerfelt: uebung.fehlersuche, siehe bereiteVor().
export default {
    // Der Container reicht allen Ansichten dieselben Props; was diese nicht
    // braucht, soll nicht als HTML-Attribut am Wurzelelement landen.
    inheritAttrs: false,
    components: { ZugSortieren, AblaufKopf, ZugStepper },
    props: {
        uebung: {type: Object, required: true},
        leerSperren: {type: Boolean, default: false}
    },
    emits: ['geprueft'],
    setup(props, { emit }) {
        const { fehler } = props.uebung.fehlersuche

        const aktuell = ref(0)
        const zug = computed(() => props.uebung.zuege[aktuell.value])
        const erledigt = computed(() => props.uebung.zuege.slice(0, aktuell.value))

        const vorgabe = computed(() => {
            const z = zug.value
            const ablage = fehler[z.nr] ? fehler[z.nr].teile : z.teile
            // Ueber die id, nicht das Objekt: aus dem Speicher der Sitzung
            // kommen Kopien zurueck.
            const vorrat = [...z.teile, ...z.ablenker].filter(t => !ablage.some(a => a.id === t.id))
            return { ablage, vorrat: mische(vorrat) }
        })

        const rueckmeldung = ref(null)
        const spruchVon = (z) => z.teile.map(t => t.text).join(' ')
        const einzug = einzugFuer(props.uebung)

        const pruefen = (ablage) => {
            const ergebnis = pruefe(ablage, zug.value)
            const letzter = aktuell.value === props.uebung.zuege.length - 1

            emit('geprueft', { sitzt: ergebnis.sitzt, geloest: ergebnis.sitzt && letzter })

            if (ergebnis.sitzt && !letzter) {
                aktuell.value += 1
                rueckmeldung.value = null
                return
            }
            rueckmeldung.value = {
                sitzt: ergebnis.sitzt,
                spruch: ablage.map(t => t.text).join(' '),
                regeln: [...new Set(ergebnis.bausteine.map(b => b.hinweis).filter(Boolean))]
            }
        }

        return { zug, erledigt, vorgabe, rueckmeldung, spruchVon, einzug, pruefen }
    }
}
</script>

<style scoped>
/* Station = Farbe Blau, wie ueberall: Farbe steht fuer Station oder fuer
   richtig/falsch, fuer nichts sonst. */
.station {
    margin: 0 0 0.75rem;
    font-size: 0.8rem;
    font-weight: 650;
    color: var(--zs-blau);
}

.auftrag {
    margin: 0 0 1.25rem;
    font-weight: 600;
    color: var(--zs-tinte);
}

/* Fertige Zuege: stehen fest, olivgruen = richtig. */
.erledigt {
    margin: 0 0 1rem;
    padding: 0.7rem 0.9rem;
    background: var(--zs-oliv-hell);
    border: 1px solid rgba(85, 107, 47, 0.4);
    border-radius: var(--zs-ecke);
}

.erledigt .spruch {
    margin: 0;
    line-height: 1.45;
}

/* Funktext in fester Laufweite: Protokolltext, nicht Bedientext. */
.erledigt .spruch {
    font-family: var(--zs-funkschrift);
}
</style>
